import React from "react";
import { useSelector } from "react-redux";

import { CheckBoxField, DisplayOptions, SectionTitle, SeparatorBar } from "../../../components";
import { PlayerModesPicker } from "../../../components/PlayerModesPicker";
import { SubForm, SubFormNavigationProps } from "../../../components/SubForm";
import {
  gameInfoConfigurationsInitialValues,
  useIdXboxGameInformationConfigurationsValidationSchema,
} from "../../../helpers/formValidation";
import { useStrings } from "../../../localization/localizationHelpers";
import { CrossPlay } from "../../../models/crossPlay";
import { LocalPlayerModes, OnlinePlayerModes } from "../../../models/playerModes";
import { getIsSavingForm } from "../../../redux/selectors";
import { GameInformationFormId, GameInformationSubFormsId } from "../GameInformationPageConstants";

const ConfigurationsSubForm = (props: SubFormNavigationProps) => {
  const [
    playerModes,
    localPlayerModes,
    singleMultiplayer,
    multiplayer,
    couchCoOp,
    onlinePlayerModes,
    onlineSinglePlayer,
    onlineMultiplayer,
    onlineCoOp,
    compatibility,
    crossPlay,
    windowsStoreVersions,
    win32Versions,
    playStationVersions,
    nintendoSwitchVersions,
    otherVersions,
  ] = useStrings(
    "playerModes",
    "localPlayerModes",
    "singleMultiplayer",
    "multiplayer",
    "couchCoOp",
    "onlinePlayerModes",
    "onlineSinglePlayer",
    "onlineMultiplayer",
    "onlineCoOp",
    "compatibility",
    "crossPlay",
    "windowsStoreVersions",
    "win32Versions",
    "playStationVersions",
    "nintendoSwitchVersions",
    "otherVersions"
  );
  const validationSchema = useIdXboxGameInformationConfigurationsValidationSchema();
  const playerOptions2To8 = [2, 3, 4, 5, 6, 7, 8];
  const playerOptions1To8 = [1, 2, 3, 4, 5, 6, 7, 8];
  const isSavingFormToCrm = useSelector(getIsSavingForm);

  return (
    <SubForm
      validateConfig={validationSchema}
      initialValues={gameInfoConfigurationsInitialValues}
      formId={GameInformationFormId}
      subFormId={GameInformationSubFormsId.Configurations}
      {...props}
    >
      <SectionTitle title={playerModes} />
      <PlayerModesPicker
        name="localPlayerModes"
        title={localPlayerModes}
        playerModes={[
          {
            value: LocalPlayerModes.singleMultiplayer,
            label: singleMultiplayer,
            playersPerConsoleOptions: [],
          },
          {
            value: LocalPlayerModes.multiplayer,
            label: multiplayer,
            playersPerConsoleOptions: playerOptions2To8,
          },
          {
            value: LocalPlayerModes.couchCoOp,
            label: couchCoOp,
            playersPerConsoleOptions: playerOptions2To8,
          },
        ]}
        disabled={isSavingFormToCrm}
      />
      <PlayerModesPicker
        name="onlinePlayerModes"
        title={onlinePlayerModes}
        playerModes={[
          {
            value: OnlinePlayerModes.onlineSinglePlayer,
            label: onlineSinglePlayer,
            playersPerConsoleOptions: [],
          },
          {
            value: OnlinePlayerModes.onlineMultiplayer,
            label: onlineMultiplayer,
            playersPerConsoleOptions: playerOptions1To8,
          },
          {
            value: OnlinePlayerModes.onlineCoOp,
            label: onlineCoOp,
            playersPerConsoleOptions: playerOptions1To8,
          },
        ]}
        disabled={isSavingFormToCrm}
      />
      <SeparatorBar />
      <SectionTitle title={compatibility} />
      <CheckBoxField
        checkBoxes={[
          {
            value: CrossPlay.windowsStoreVersions,
            label: windowsStoreVersions,
          },
          {
            value: CrossPlay.win32Versions,
            label: win32Versions,
          },
          {
            value: CrossPlay.playStationVersions,
            label: playStationVersions,
          },
          {
            value: CrossPlay.nintendoSwitchVersions,
            label: nintendoSwitchVersions,
          },
          {
            value: CrossPlay.otherVersions,
            label: otherVersions,
          },
        ]}
        name={"crossPlay"}
        title={crossPlay}
        display={DisplayOptions.column}
        isSaving={isSavingFormToCrm}
      />
    </SubForm>
  );
};

export default ConfigurationsSubForm;
